import React from "react";
import { CARD_LANGUAGE_NAMES, CARD_LANGUAGES, GAMES, GRADE_NAMES, GRADES, KNOWN_GAMES, languagesForGames } from "./data.jsx";
import { api, expansionNameFor, suggestionNameFor } from "./api.jsx";
import { useApp } from "./store.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";
import { PictureReport } from "./picture-report.jsx";
import { SetReport } from "./set-report.jsx";
import { BackLink, SectionHead, MarketFollowFields } from "./chrome.jsx";

const { Button, IconButton, Icon, Badge, Card, Input, Select, Radio, Checkbox, Tag, ListingCard, FluteDivider, EmptyState } = window.CardboardmarketDesignSystem_2a3239;

/**
 * How many chips are laid out before the picker becomes a dropdown.
 *
 * COUNTED IN SETS, because that is what a chip is now. The server sends one
 * entry per set wherever the pictures cannot separate two products of one, so
 * ST15-002's ten printings arrive as eight chips — see /api/catalog/printings.
 *
 * Recounted on 2026-09-07 over every name group holding more than one listable
 * product whose printings all carry ONE picture — the groups this layout draws
 * at all, 34,643 of them — by the number of SETS they span:
 *
 *     ≤ 6 sets    30,960   89.4%
 *     ≤ 12        33,801   97.6%
 *     ≤ 20        34,395   99.3%
 *     largest        113
 *
 * Twelve is where the tail starts. Below it the whole list is on screen in at
 * most three rows on a phone; above it a chip row becomes a wall that pushes the
 * rest of the form off the screen, and a one-line dropdown is the same height at
 * 113 sets as it is at 13.
 *
 * The number did not move and the picture under it did: collapsing duplicates
 * put 1,214 groups that used to need the dropdown back into chips, and the
 * largest list shrank from 178 to 113. One Piece — the game this layout exists
 * for — now tops out at 17 sets and keeps 2,837 of its 2,838 groups in chips.
 */
const PRINTING_CHIP_LIMIT = 12;

/**
 * How many versions are laid out at once before the rest go behind one reveal.
 *
 * The version picker is pictures wherever it can be — a drawing cannot be chosen
 * from a dropdown of its own name — so its ceiling is a REVEAL rather than a
 * different control. Twelve, the same number as the printings, because it is the
 * same question about the same screen: how much can stand between the card field
 * and the rest of the form.
 *
 * Nothing reaches it today. `card_art` holds 4,843 rows over 997 codes with more
 * than one drawing; the largest is nine, twelve have more than six and none has
 * more than twelve. The other shape this control takes is one tile per product
 * of a set, and the worst in the whole catalogue is ST03-008's eight. It is here
 * so that a publisher who draws a card twenty times lands on a bounded screen
 * rather than on whatever the grid does with twenty tiles.
 */
const VERSION_TILE_LIMIT = 12;

/**
 * How many PRINTING tiles are drawn before the rest are behind a reveal.
 *
 * Magic is the reason. `Lightning Bolt` has 68 products across 51 sets and 64
 * distinct pictures, and because they differ this picker draws pictures — so a
 * seller met sixty-eight card images at once. That is a wall to read before it is
 * anything else, and it is also sixty-eight files fetched to answer one question.
 *
 * TWELVE, WHICH IS THE NUMBER THIS SCREEN ALREADY USES. `PRINTING_CHIP_LIMIT` is
 * twelve and `VERSION_TILE_LIMIT` is twelve, and a reader meeting two reveals on
 * one screen should not meet two different affordances. It is also what the row
 * holds: a tile is 116 px wide with an 8 px gap, so twelve is three full rows in
 * the 1116 px content box and six on a phone — enough to see the shapes differ,
 * bounded enough to read.
 *
 * The CHIP layout is deliberately not capped here. Above twelve chips it already
 * becomes a `Select`, which is a better answer for a list of names than a reveal,
 * and disturbing it would be replacing a working control with a second one.
 */
const PRINTING_TILE_LIMIT = 12;

/**
 * THE SETS IN THE ORDER THEY ARE READ, not the order their English names sort in.
 *
 * The server orders /api/catalog/printings by the English set name, and since
 * 3a5dd29 a Spanish interface draws the Spanish one — so "Décima Edición" sat
 * between Summer Magic and Unlimited. This re-sorts by the label shown, keeping
 * the server's own rules: a set with no name last, a "(Non-English)" twin beside
 * its set, then price descending (none last), then id. Display order only.
 *
 * THE TWIN SORTS UNDER ITS SET'S LABEL. Cardmarket files "Royal Blood
 * (Non-English)" as its own expansion, and on pre none of those carry a Spanish
 * name — sorted by its own label it would land under R, away from "Sangre Real".
 * So a group, keyed by the English name without the suffix, is read by whichever
 * member translates it.
 */
const NON_ENGLISH = ' (Non-English)';
export function orderPrintingsByShownSet(items, locale) {
  const keyOf = (p) => (p.expansion || '').replace(NON_ENGLISH, '').trim();
  const labelOf = new Map();
  for (const p of items) {
    const key = keyOf(p);
    if (!key) continue;
    const shown = (expansionNameFor(p, locale) || '').replace(NON_ENGLISH, '').trim();
    if (shown && shown !== key) labelOf.set(key, shown);
    else if (!labelOf.has(key)) labelOf.set(key, key);
  }
  const collator = new Intl.Collator(locale);
  const price = (p) => (p.priceCents == null ? -Infinity : p.priceCents);
  return [...items].sort((a, b) => {
    const ka = keyOf(a), kb = keyOf(b);
    return (!ka - !kb)
      || collator.compare(labelOf.get(ka) || '', labelOf.get(kb) || '')
      // Two English sets one Spanish name: still each kept in one piece.
      || (ka < kb ? -1 : ka > kb ? 1 : 0)
      || price(b) - price(a)
      || a.productId - b.productId;
  });
}

export function PostScreen({ initialGame } = {}) {
  const { publishListing, profile, radius, languages, go, t, locale } = useApp();
  /*
   * No game until somebody picks one, and none shown as picked either.
   *
   * This used to default to One Piece. The chip rendered as selected while the
   * search deliberately ignored it, so somebody who wanted One Piece — and could
   * see One Piece highlighted — was shown Pokémon results and reported it as
   * broken. They were right: the screen said one thing and the code did another.
   *
   * Nothing selected is the honest version of "searching everything", which is
   * the behaviour that was already there and worth keeping: filtering by a
   * DEFAULT is a trap, because a Magic card typed into a form that quietly
   * assumed One Piece looked as though it did not exist.
   */
  /*
   * `copies` opens at '1' and is held as a STRING, like `price` beside it.
   *
   * One is the honest default rather than a guess: it is what every listing was
   * before stock existed, and it is what most listings are. A seller with a
   * playset changes it here rather than posting the same card four times, which
   * is what they had to do before — four listings, four conversations, four
   * cards to four meetings.
   */
  /*
   * `language` opens EMPTY, and that is not laziness.
   *
   * Condition defaults to NM because near mint is what most posted cards are and
   * a seller who disagrees is looking at the card. Language has no such answer:
   * defaulting to English would put "English" on a Japanese card belonging to
   * somebody who never touched the control, and it would look exactly as
   * deliberate as one they chose — the same mistake the game default made when
   * it filed a Magic card under One Piece.
   *
   * Empty no longer means "nobody has said", because that stopped being a value
   * the column can hold — migration 050 made it NOT NULL. It means NOT YET
   * ANSWERED, which is why it is in the `ready` gate below rather than being
   * sent as a null, and why the picker opens on a prompt rather than a language.
   */
  /*
   * The ONE inference in this form, and it is narrow on purpose.
   *
   * A trader who has said they collect in exactly one language has stated a fact
   * about their own collection, and re-typing it on every card they post is work
   * we made them do with something we already knew. That is what makes this
   * different from the English default migration 050 refused: that would have
   * been US deciding, about somebody else's card, from nothing.
   *
   * TWO OR MORE preferences infer NOTHING. There is no "most likely" among them —
   * a trader who reads English and Japanese posts both — so the picker opens on
   * its prompt and they choose, exactly as before. Never guess between
   * preferences; that is a coin toss wearing a default's clothes.
   *
   * ZERO preferences is "all languages", which is not one language either, so it
   * falls through the same gate and pre-fills nothing.
   *
   * Pre-filled is not locked: the picker is right there and changing it is one
   * tap, which is the whole test for whether a default is allowed to exist.
   */
  const soleLanguage = languages.length === 1 ? languages[0] : '';
  /*
   * THE ONE GAME DEFAULT, and it is not ours either. A shop's back office opens
   * this form with the game its scope names when the scope names exactly one
   * (`ShopShell`), and that is a choice the shop made on its own scope bar a
   * moment earlier — which is what separates it from the One Piece default
   * above: that was the screen deciding, this is the seller having said. A
   * trader's form is given no game and starts empty, as it has since that
   * default went.
   *
   * THE PROP'S CONTRACT is "a game id, or nothing", and nothing is what any
   * other value means: the chips below can only show a game the product
   * knows, and the route would refuse one it does not, so a value outside the
   * list is no default rather than a chip nothing matches. The same test the
   * shell applies to a game from the address — `KNOWN_GAMES`: one rule, one
   * idiom.
   *
   * READ ONCE, as the initial value. A scope change while the form is open —
   * the bar is drawn above it — changes the prop and moves nothing here: the
   * game chosen is the seller's, and an initial value the seller can change
   * is not one the screen takes back. One tap on the chips changes it, which
   * is the test a default has to pass.
   */
  const firstGame = KNOWN_GAMES.has(initialGame) ? initialGame : '';
  // `foil` starts false rather than undefined: it is a real answer, not a
  // question nobody has been asked, and the box has to render unticked from the
  // first paint or React calls the input uncontrolled on the tick after.
  const [f, setF] = React.useState({ game: firstGame, title: '', set: '', condition: 'NM', language: soleLanguage, mode: 'sale', price: '', note: '', copies: '1', foil: false });
  /*
   * ONCE, and only into an empty field.
   *
   * The initialiser above runs on the first render, which can be before the
   * account's preferences have arrived — the provider's effect that reads them
   * off the trader runs after this component's own. So the seed is repeated
   * here for that case, guarded by a ref so it cannot fire twice and by the
   * empty check so it cannot overwrite an answer somebody has given. Without
   * both, choosing the prompt again would silently be undone by this effect,
   * which is the failure mode of every form that fills itself in.
   */
  const seededLanguage = React.useRef(Boolean(soleLanguage));
  React.useEffect(() => {
    if (seededLanguage.current || languages.length !== 1) return;
    seededLanguage.current = true;
    setF((prev) => (prev.language ? prev : { ...prev, language: languages[0] }));
  }, [languages]);
  /**
   * The CARD that was picked from the catalogue, and how many printings it has.
   *
   * Kept as state rather than re-parsed out of the title. Reading the code back out
   * of "Zoro-Juurou (OP05-067)" works right up until a card has brackets in its
   * actual name, and then it silently produces nonsense.
   *
   * `count` arrives with the suggestion, before any printing has been fetched, so
   * the picker below can take its full size immediately instead of appearing under
   * the reader's thumb a moment later.
   */
  const [card, setCard] = React.useState({ productId: null, code: null, count: 0, sets: 0, imagesDiffer: false });
  /**
   * The PRINTING being listed: the catalogue product, and its picture.
   *
   * These two and `f.set` are one fact in three fields, and they used to be able to
   * disagree. The set chips wrote only the text, so choosing "The Best Vol. 2" left
   * the listing pointing at the Starter Deck product and showing the Starter Deck
   * picture — the words said one card and the link meant another. Nothing sets any
   * one of them alone any more; `choosePrinting` below sets all three or none.
   */
  const [picked, setPicked] = React.useState({ productId: null, imageUrl: null, imageSrcset: null });
  /** The name the picked card came with; the title has to still say it for the pick to count. */
  const [pickedTitle, setPickedTitle] = React.useState(null);
  /*
   * The pick's ENGLISH name, which is what the listing stores as its title.
   * The box shows the name the seller found it by — "Elfos de Llanowar" — but
   * a card's title is the catalogue's English name, which wants, restocks and
   * every search key on; the card's own language is shown on read (migration
   * 157), never stored.
   */
  const [pickedName, setPickedName] = React.useState(null);
  /** Every printing of the picked card. Fetched once, when a card is picked. */
  const [printings, setPrintings] = React.useState({ loading: false, items: [], total: 0, shown: 0, imagesDiffer: false });
  /**
   * The ARTWORKS the publisher gives this card, and which one is in the seller's
   * hand.
   *
   * Deliberately NOT folded into `picked` above. The chip decides the catalogue
   * link, the set line and the price; this says WHICH COPY of that set, which is
   * a second question with a second answer.
   */
  /**
   * WHICH COPY OF THIS CARD, INSIDE THE CHOSEN SET — one question, one answer.
   *
   * This used to be two fields feeding two controls: `versionId` for "which
   * version is yours?" and `artKey` for "which artwork is yours?". The owner
   * read them the way anybody would:
   *
   *   "I believe those are the same thing. When Cardmarket talks about
   *    'versions', they mean alternate art cards/reprints."
   *
   * They are, so there is one control and one field. A tile carries both a
   * product and a drawing, and clicking it answers both at once — which is the
   * defect the pair had: nothing stopped a seller choosing V.1 in one control
   * and the alternate art's picture in the other, and the listing stored the
   * contradiction.
   *
   * NULL UNTIL SOMEBODY CLICKS, and nothing is chosen on arrival. A default here
   * would be us claiming something about a card in somebody else's hand — the
   * rule the artwork picker always had — and the preview now shows a frame
   * saying the picture is missing until the claim is made, which is honest and
   * is the thing that asks for the click.
   */
  const [versionKey, setVersionKey] = React.useState(null);
  /**
   * Whether a chosen version was dropped because the printing moved away from it.
   *
   * A claim that is no longer on offer must not survive as a hidden field — that
   * is a listing carrying a picture nobody can see they chose. So it is cleared,
   * and clearing it is SAID, because the alternative is a seller scrolling back
   * to a picker that has quietly forgotten their answer.
   */
  const [versionDropped, setVersionDropped] = React.useState(false);
  /*
   * Whether the language was filled in BY the set rather than by the seller.
   *
   * Same reasoning as `versionDropped` beside it: a form that changes a field
   * somebody can see, and says nothing, is a form they stop trusting. This one
   * is the gentler half of that pair — it reports a field FILLED rather than
   * dropped — and it matters most for the seller who had already picked a
   * language and whose answer was replaced.
   *
   * Holds the TAG it wrote rather than a boolean, so the sentence can name the
   * language instead of saying "a language" — and so the hand-back below knows
   * whether the value still in the field is one this put there.
   */
  const [languageFromSet, setLanguageFromSet] = React.useState(null);

  /**
   * The set line, written the way a person writes it: the printed name first, the
   * card's own number in brackets after it.
   *
   * "Awakening of the New Era [OP05-067]" is what someone would put on a listing.
   * A bare code is what a database would.
   *
   * A few thousand catalogue products carry no expansion at all. Those fall back to
   * the printed code, and to nothing when there is not even that — an empty string
   * the callers below read as "leave what is already there", rather than writing
   * the word "null" onto somebody's listing.
   */
  const setLine = (expansion, code) => (code ? `${expansion} [${code}]` : expansion);
  const lineFor = (expansion, code) => (expansion ? setLine(expansion, code) : (code || ''));

  /**
   * THE SET CAN ANSWER THE LANGUAGE, AND WHERE IT DOES, IT SHOULD.
   *
   * The owner: "at least for one piece where there's specific sets that go with
   * specific languages like the JP ones, we should automatically select the
   * language as JP."
   *
   * Cardmarket sells a Japanese print run as its own set — "Romance Dawn
   * (Japanese)", "Paramount War (Japanese)" — so a seller who picks one has
   * already said what language they hold. Asking again is asking a question
   * they just answered, and leaving it blank is worse than that: the language
   * is required, so the form sits with the post button disabled over a fact
   * that is written on the chip they tapped.
   *
   * ONLY A SUFFIX THAT NAMES A LANGUAGE, which is two of them. Counted over
   * every parenthetical the catalogue ends a set name with: "(Japanese)" on 17
   * sets and "(Korean)" on 11, and those two are the whole list.
   *
   * THE THREE THAT LOOK LIKE THEY BELONG HERE AND DO NOT:
   *
   *  * "(OCG)", on 141 sets — the Asian release of Yu-Gi-Oh!, printed in
   *    Japanese AND Korean AND Chinese. It names a game, not a language, and
   *    guessing Japanese from it would be wrong for two of the three.
   *  * "(Asia Region Legal)", on 17 — a REGION, whose cards are English. It is
   *    the one that prompted all this, and reading a language out of it is
   *    exactly the mistake to avoid.
   *  * "(Non-English)" — says only what the language is NOT, and `setNameOf` on
   *    the server strips it before it ever arrives.
   *
   * The picture cannot help here and never will: CardTrader models one blueprint
   * for a set and its Japanese twin, so both draw the same English scan. Checked
   * on Romance Dawn OP01-001 — base and (Japanese) both resolve to blueprint
   * 244455. The set name is the only signal there is.
   */
  const SET_SUFFIX_LANGUAGES = { Japanese: 'ja', Korean: 'ko' };
  const languageOfSet = (expansion) => {
    // Anchored at the end, so "Japanese Collection" and a mid-name mention are
    // both left alone. Only a trailing parenthetical is Cardmarket's marker.
    const suffix = /\(([^)]+)\)\s*$/.exec(expansion || '');
    return (suffix && SET_SUFFIX_LANGUAGES[suffix[1]]) || null;
  };

  /**
   * Choosing a printing. THE one place the three fields move, and they move together.
   *
   * The set line, the catalogue link and the picture are the same decision written
   * three ways: what the seller reads, what a buyer's search matches, and what the
   * preview shows them to confirm it. Anything that changes one of them and not the
   * others reintroduces the bug this replaces.
   */
  const choosePrinting = (printing) => {
    const line = lineFor(printing.setName, card.code);
    setPicked({ productId: printing.productId, imageUrl: printing.imageUrl, imageSrcset: printing.imageSrcset || null });
    /*
     * AND THE COPY GOES WITH THE SET IT WAS READ IN.
     *
     * A version answers "which of this set's copies", so it cannot survive the
     * set changing: "V.2" of The Best Vol.2 means nothing in Royal Blood, and
     * the drawing on that tile was proved by a set the seller has just left.
     * Carrying either across would be a hidden field disagreeing with a visible
     * picker, which is the class of defect 153e758 and 4bb8afe both close.
     *
     * SAID OUT LOUD, and only when there was something to lose. A picker that
     * has quietly forgotten an answer is worse than one that says it has.
     */
    /*
     * AND THE LANGUAGE, WHERE THE SET HAS ALREADY SAID IT.
     *
     * Written through the same `setF` as the set line rather than in an effect
     * watching `picked`, so it lands in the SAME render as the set that implies
     * it. An effect would show the seller a Japanese set beside an English
     * language for a frame, which is the form disagreeing with itself in public.
     *
     * It overwrites a language already chosen, deliberately: the set is the
     * later and more specific answer, and the alternative is a listing that
     * says Romance Dawn (Japanese) in English. The control stays editable
     * underneath — a seller who means something else can still say so — and
     * `languageFromSet` below tells them it happened rather than letting a
     * field change itself quietly.
     *
     * AND IT IS HANDED BACK WHEN THE SET STOPS SAYING IT, which is the half
     * that makes the rule honest rather than a one-way write. Pick the Japanese
     * set, then move to the English one, and a `ja` left sitting there is the
     * same stale answer `versionDropped` exists to prevent — except this one
     * still posts, because the field is full. So a value this put there is
     * taken back out, and the seed the form opened with returns: the trader's
     * own single language where they have one, and the prompt otherwise.
     *
     * A language the SELLER typed is never touched on the way out — only the
     * one we wrote, which `languageFromSet` is the record of.
     */
    const implied = languageOfSet(printing.setName);
    setF((prev) => ({
      ...prev,
      set: line || prev.set,
      ...(implied ? { language: implied } : languageFromSet ? { language: soleLanguage } : null),
    }));
    setLanguageFromSet(implied);
    setVersionDropped(Boolean(versionKey));
    setVersionKey(null);
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  /*
   * The price, in the unit everything else on the way to the server uses.
   *
   * Read ONCE, here, and then used by all three of the things that have to
   * agree about it: the refusal below, the preview card, and the body posted to
   * the API. They each used to do their own `Math.round(+f.price * 100)` on the
   * same string, which is three chances to disagree the day the field learns a
   * character `+` cannot read — and it just has, because a price can now carry
   * a decimal separator and `+"0,57"` is NaN.
   *
   * `null` where nothing has been said, which is why the refusal tests
   * `f.price !== ''` separately: a box somebody has not typed in yet is not an
   * error, and a box holding only «,» is.
   */
  const priceCents = f.mode === 'trade' ? null : api.centsFromMoney(f.price);

  const priceError = f.mode !== 'trade' && f.price !== '' && !(priceCents > 0) ? 'Give a price above 0€ or list it for trade.' : undefined;
  /*
   * Photos are held here as File objects until the listing exists.
   *
   * They cannot be uploaded before there is something to attach them to, so the
   * form carries them and the store does create -> upload -> publish in order.
   * Previews come from object URLs, which have to be revoked or the page leaks a
   * few megabytes per photo somebody changed their mind about.
   */
  /*
   * Whether the GAME was chosen, or is just the value the form opened on.
   *
   * The filter now follows the state of the chips exactly: nothing selected
   * searches everything, and a selected game searches that game. `gameChosen`
   * used to carry this separately from `f.game`, which is how the two came
   * apart — a chip could look selected while the search behaved as though
   * nothing was.
   */
  /*
   * The printings, fetched when a CARD is picked and not when a printing is.
   *
   * Keyed on `card.productId`, which only the autocomplete moves — choosing a
   * printing changes `picked.productId`, and hanging this off that would refetch
   * the same list on every click and flash the picker back to its waiting state.
   *
   * Aborted like the typeahead it follows: picking a second card before the first
   * one's printings land must not leave the wrong card's printings on screen.
   */
  // Read when the answer lands, which may be after the interface switched.
  const localeNow = React.useRef(locale);
  React.useEffect(() => {
    // One question now, and one count that decides whether to ask it: how many
    // printings the catalogue holds. The drawings used to be a second, and the
    // owner retired them.
    if (!card.productId || card.count < 2) {
      setPrintings({ loading: false, items: [], total: 0, shown: 0, imagesDiffer: false });
      return;
    }
    const controller = new AbortController();
    setPrintings({ loading: true, items: [], total: card.count, shown: 0, imagesDiffer: card.imagesDiffer });
    api.cardPrintings({ productId: card.productId }, controller.signal)
      .then((r) => {
        setPrintings({
          loading: false, items: orderPrintingsByShownSet(r.printings, localeNow.current), total: r.total,
          // How many PRINTINGS the answer covered, which is not how many entries
          // it holds: an entry is a set wherever the pictures cannot separate two
          // products of one. Only this number can say whether anything was cut.
          shown: r.shown ?? r.printings.length,
          imagesDiffer: Boolean(r.imagesDiffer),
        });
      })
      // A failure here costs the CHOICE, not the post: the set line and the
      // catalogue link the suggestion already agreed on both stand, and a
      // version nobody was offered is one nobody claimed.
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setPrintings({ loading: false, items: [], total: 0, shown: 0, imagesDiffer: false });
      });
    return () => controller.abort();
  }, [card.productId, card.count]);
  // The interface switched mid-form: the same sets, in the order the new labels read.
  React.useEffect(() => {
    if (localeNow.current === locale) return;
    localeNow.current = locale;
    setPrintings((p) => ({ ...p, items: orderPrintingsByShownSet(p.items, locale) }));
  }, [locale]);

  /*
   * WHETHER THE PICTURE EARNS ITS PLACE in the printing picker.
   *
   * One Piece maps every product sharing a card code onto that code's single
   * artwork, so ten printings of ST15-002 carried ten copies of one picture with
   * different set names under them. Ten identical pictures do not merely waste
   * the room: they IMPLY a difference that is not there, which is the opposite
   * of what a picker is for. Magic is the other case entirely — 19,880 of its
   * 22,805 multi-product name groups have printings that genuinely look
   * different, and there the picture is the only thing that separates them.
   *
   * So it is a question about the data, asked of the data: do these printings
   * differ by picture at all? Asked of what is actually on screen rather than of
   * the whole group, because a truncated list showing two hundred identical
   * pictures is still two hundred identical pictures.
   *
   * The suggestion's own answer stands in while the list is in flight, so the
   * waiting tiles are the shape and size of the real ones instead of resizing
   * under the reader's thumb a moment later.
   */
  // The SERVER's answer in both states, never a count of the URLs it sent.
  //
  // This used to count distinct `imageUrl`s once the list landed, which was true
  // right up until the artworks were mirrored: our CDN keeps one file per
  // product, so ten products sharing one picture arrive with ten distinct URLs
  // and the count says "different" for every card in the catalogue. The server
  // asks the publisher's address instead — see /api/catalog/printings.
  /*
   * PICTURES HERE ONLY WHERE NOTHING ELSE CAN CARRY THEM.
   *
   * `imagesDiffer` alone was the wrong test, and it broke the moment the ingest
   * was fixed: giving each product its own drawing made One Piece's printings
   * differ by image, so this picker went straight back to tiles — the layout the
   * owner had already asked twice to be rid of. The rule was right for the
   * problem it was written for and wrong as a general one.
   *
   * The owner's model is the one to encode: "the printing is a fact, the artwork
   * is a look", and "keep the visual picking showing the image only for the
   * artwork". So a card that HAS a versions picker never shows pictures here —
   * that picker is where looking belongs, and showing them twice is the
   * duplication they objected to.
   *
   * A card whose set holds a single copy keeps its pictures here: there is no
   * second control to move the looking to, and the picture is then the only
   * thing separating two printings — 18,776 of Magic's 22,808.
   */
  /*
   * WHETHER THE VERSION CONTROL IS ON SCREEN, which decides whether the printing
   * chips also draw pictures.
   *
   * ASKED OF THE WHOLE CARD AND NEVER OF THE CHOSEN CHIP, and that is a layout
   * rule rather than a semantic one. The control appears for a chip whose set
   * sells the card more than once; deciding this per chip would add and remove
   * eight pictures as the seller clicked between chips, moving everything below
   * them. A card either has this question somewhere in it or it does not.
   *
   * The suggestion's counts while the list is in flight — more printings than
   * sets means some set holds two — and the list itself once it lands. This used
   * to read a count of the card's ARTWORKS, which was the old second question.
   */
  const hasVersionsPicker = printings.loading
    ? card.count > card.sets
    : printings.items.some((p) => (p.versions?.length ?? 0) > 1);
  const showPrintingImagesIfRoom =
    !hasVersionsPicker && (printings.loading ? card.imagesDiffer : printings.imagesDiffer);

  /*
   * HOW MANY ENTRIES THE PICKER IS ABOUT TO DRAW, which decides the control and
   * the shape of the wait.
   *
   * The suggestion's own counts while the list is in flight, and the list itself
   * once it lands — for the same reason `showPrintingImages` reads the suggestion
   * first: a control that changes KIND under the reader's thumb is worse than one
   * that waits.
   *
   * TWO COUNTS, AND THE PICTURES CHOOSE BETWEEN THEM. Where the pictures separate
   * the products, an entry is a product and the number is `card.count`. Where
   * they do not, the server sends one entry per SET and the number is
   * `card.sets` — ST15-002 is ten printings and eight sets, and laying out ten
   * waiting chips for eight real ones moves every field below them when the
   * answer arrives.
   *
   * Both group by name where the printings endpoint groups by name AND game, so
   * either can promise more than arrives — which moves a card sitting on the
   * boundary from a dropdown to chips, never the other way, and chips can absorb
   * that. `card.sets` falls back to `card.count` for a suggestion served by an
   * older API than this build, which over-counts in the same forgiving direction.
   */
  const printingCount = printings.loading
    ? ((showPrintingImagesIfRoom ? card.count : card.sets) || card.count || 2)
    : printings.items.length;

  /*
   * CHIPS, OR A DROPDOWN, and where the line between them sits.
   *
   * Where the pictures say nothing the question is "which set is it", and a set
   * name is a word — so it belongs in a row of chips, which is what the games
   * and the languages already use, and not in a full-width row per printing
   * borrowing the shape of a picture picker that is not being drawn.
   *
   * That works until it does not. Counted over the whole catalogue on 2026-09-07,
   * of the 34,643 name groups whose printings carry ONE picture — the groups
   * this branch draws — 30,960 span six sets or fewer and 33,801 span twelve or
   * fewer, while the largest spans 113. So twelve is where 97.6% of them are
   * complete and the rest become a wall: thirteen set names is four rows of
   * chips on a phone before anything below them can be reached.
   *
   * Above it, a `<select>`. Not a "show more" reveal like ChipPicker's: that
   * control exists to keep a signup step's primary action above the fold and it
   * still ends in the wall it delayed, whereas a dropdown is one line high at
   * 113 sets exactly as it is at 13. One Piece — the game this branch was
   * written for — tops out at 17 sets and keeps 2,837 of its 2,838 groups in
   * chips; Yu-Gi-Oh's 113 is what the dropdown is for.
   */
  /*
   * AND THE CEILING IS ON BOTH LAYOUTS, which it was not.
   *
   * This read `!showPrintingImages && count > 12`, so the limit only ever
   * applied to the words. That held while "the pictures differ" meant a handful
   * of Magic alternate arts. It stopped holding the day the printings started
   * carrying their own pictures: Blue-Eyes White Dragon has 178 printings across
   * 103 sets and drew 142 picture tiles, which is not a picker, it is a wall.
   * The owner said so within minutes of seeing it.
   *
   * A dropdown is one line high at 142 exactly as it is at 13, and nothing is
   * lost by it — choosing a printing updates the preview beside the form, so the
   * seller still sees the card they picked, at a size worth looking at. That is
   * the division the owner asked for in the first place: the printing is a fact
   * and belongs in words, the picture is the confirmation.
   */
  /**
   * WHICH ENTRY IS SELECTED, which is not the same question as "what is
   * `picked.productId`" and this is the bug that made the screen lie.
   *
   * REPORTED, with a screenshot: the set line read "Core 2019" and the picker
   * under it read "30th Anniversary Edition". Two different sets, on one card,
   * at the same moment.
   *
   * The picker holds ONE ENTRY PER SET, keyed by a representative product —
   * Core 2019's entry is product 360262. But `picked.productId` is whatever was
   * actually chosen, and two of the three things that set it choose a SIBLING:
   * the version tiles below pick Island 267, and a scan picks whichever row the
   * seller tapped. Neither is the representative, so the `value` matched no
   * option — and a `<select>` whose value matches nothing displays its FIRST
   * option. "30th Anniversary Edition" was never chosen by anybody; it is
   * alphabetically first, because "3" sorts before every letter.
   *
   * Matched through `versions`, which is exact: the server sends each entry with
   * the products of its set, and 360262's entry lists 360262 through 360265. No
   * string comparison of set names, which would be a second way to be wrong.
   *
   * Nothing matching means nothing selected. An empty picker is honest; a picker
   * confidently naming a set the seller never chose is how a listing goes out
   * under the wrong edition.
   */
  const selectedPrintingValue = React.useMemo(() => {
    if (picked.productId == null) return '';
    const entry = printings.items.find((p) => p.productId === picked.productId
      || (p.versions || []).some((v) => v.productId === picked.productId));
    return entry ? String(entry.productId) : '';
  }, [picked.productId, printings.items]);

  const usePrintingSelect = printingCount > PRINTING_CHIP_LIMIT;
  /*
   * WHETHER THE PICKER BELOW IS ANSWERING "WHICH SET", which is the one question
   * the text box above would otherwise be asking as well. Read once, so the two
   * cannot disagree about who owns the question — and so a card with a single
   * printing, which draws no picker, still gets a box rather than a dead label.
   */


  /*
   * WHO OWNS THE "WHICH SET" QUESTION on this render — the picker below, or the
   * text box above it. Exactly one of them, always, and never neither.
   *
   * The picker owns it whenever the catalogue named a set at all, including when
   * it named exactly one: that is the case that used to fall through to a text
   * box and read as "we have no idea what this is".
   */
  const printingsPicker = !printings.loading && printings.items.length >= 1;
  // Tiles only where the dropdown has not taken over, so the two never both
  // claim the same question.
  const showPrintingImages = showPrintingImagesIfRoom && !usePrintingSelect;

  /*
   * PICTURE TILES A READER CANNOT TELL APART.
   *
   * Only ever the picture layout now, because the other one no longer has the
   * problem: where the pictures say nothing the server sends one entry per SET,
   * so two entries reading the same set name cannot arrive. See
   * /api/catalog/printings.
   *
   * Where they DO say something, two tiles can still carry the same set, the
   * same rarity and the same drawing — Magic sells alternate arts under one set
   * name — and the catalogue holds nothing that separates them but the price.
   * That is admitted below rather than papered over: a price answers "what is
   * this worth" and the question on screen is "which one are you holding".
   *
   * Keyed on WHAT THE READER CAN SEE: a tile carries the set, the rarity and the
   * drawing, so all three are in the key.
   */
  const printingSeenKey = (p) => `${p.expansion || ''}|${p.rarity || ''}|${p.imageUrl || ''}`;
  const repeatedPrintings = React.useMemo(() => {
    if (!showPrintingImages) return new Set();
    const seen = new Map();
    for (const p of printings.items) {
      const key = printingSeenKey(p);
      seen.set(key, (seen.get(key) || 0) + 1);
    }
    return new Set([...seen].filter(([, n]) => n > 1).map(([key]) => key));
  }, [printings.items, showPrintingImages]);
  const ambiguousPrintings = repeatedPrintings.size > 0;

  /* The catalogue's own words, "(Non-English)" and all: that suffix is a
     different Cardmarket expansion rather than a duplicate of the line above it,
     and frequently the only difference between two entries somebody is choosing
     between. The listing gets the tidied version regardless. */
  /**
   * Cents as the money box wants them.
   *
   * TWO DECIMALS ALWAYS. This was `String(cents / 100).replace('.', ',')`, which
   * turns €3.60 into "3,6" — parsed correctly by `centsFromMoney` and read by a
   * person as an unfinished number. It only showed up when a foil suggestion
   * happened to be a round figure.
   *
   * COMMA WHATEVER THE INTERFACE LANGUAGE IS, because this box is comma-decimal
   * by construction and `formatCents` renders for the READER's locale — seeding
   * through that opened the box at "2.39" in English and the form disagreed with
   * its own suggestion button.
   */
  const moneyBox = (cents) => api.moneyText((cents / 100).toFixed(2).replace('.', ','));

  const printingLabel = (p) => p.expansion || card.code || t("Set not named");
  /*
   * AND WHAT IT READS: the set in Spanish to a Spanish interface where we hold
   * that name (migration 157), the English otherwise. `printingLabel` stays the
   * English one on purpose — it is what the options below count collisions on,
   * and the Spanish names are one per set, so a word that is unique in English
   * is unique translated. Only what is drawn goes through this.
   */
  const printingShown = (p) => expansionNameFor(p, locale) || card.code || t("Set not named");
  /*
   * AND WHAT AN OPTION READS, WHERE THE SET NAME NAMES TWO OF THEM.
   *
   * This was `printingLabel` alone, on the grounds that "the chips are one per
   * set, so no two options can read the same word". That was true and is not:
   * an entry is now one per (set, PICTURE), so Fossil sells this card twice and
   * both options read "Fossil". Two options carrying one word are not a choice —
   * the same defect the chips were collapsed to avoid, arriving in a dropdown.
   *
   * The rarity is what separates them, and it is the word Cardmarket separates
   * them by on its own page: "Fossil — Holo Rare" against "Fossil — Rare". It is
   * only added where the set name is genuinely ambiguous, so the 97% of options
   * that need nothing keep reading as the set they are.
   *
   * Cardmarket's product number is the last resort, for the pair a set, a
   * picture and a rarity still cannot separate. It says nothing about the card,
   * which is why it is last, but two identical options say less.
   */
  const printingOptionLabel = React.useMemo(() => {
    const byName = new Map();
    for (const p of printings.items) {
      const key = printingLabel(p);
      byName.set(key, (byName.get(key) ?? 0) + 1);
    }
    const byNameAndRarity = new Map();
    for (const p of printings.items) {
      const key = `${printingLabel(p)}\u0000${p.rarity ?? ''}`;
      byNameAndRarity.set(key, (byNameAndRarity.get(key) ?? 0) + 1);
    }
    /*
     * A PRINTING'S OWN NAME, above rarity and well above a product id.
     *
     * Reported: Sabo P-105 drew two chips both reading "Promos (Japanese)". They
     * are genuinely different printings — a Chinese exclusive and a standard
     * battle promo — so collapsing them would hide a real choice. The catalogue's
     * set name simply cannot tell them apart.
     *
     * CardTrader can: "Standard Battle" and "Championship 26-27 Season 1". That
     * is what somebody holding the card can actually match against, which a
     * rarity often is not and `#884254` never is.
     */
    const byNameAndPrinting = new Map();
    for (const p of printings.items) {
      const key = `${printingLabel(p)}\u0000${p.printingName ?? ''}`;
      byNameAndPrinting.set(key, (byNameAndPrinting.get(key) ?? 0) + 1);
    }
    return (p) => {
      const base = printingLabel(p);
      const shown = printingShown(p);
      if ((byName.get(base) ?? 0) < 2) return shown;
      if (p.printingName && (byNameAndPrinting.get(`${base}\u0000${p.printingName}`) ?? 0) < 2) {
        return `${shown} — ${p.printingName}`;
      }
      if (p.rarity && (byNameAndRarity.get(`${base}\u0000${p.rarity}`) ?? 0) < 2) {
        return `${shown} — ${p.rarity}`;
      }
      /*
       * "OTHERS" RATHER THAN A NUMBER, where we simply do not know what this
       * printing is. The owner's words: "if we don't have the data to catalogue
       * a product, we should just list it under an Others chip or something".
       *
       * It is also more honest than it looks. The chip means "the rest of this
       * set, which we cannot tell apart" — which is exactly true, and a seller
       * can still reason with it: their card either says Tournament Pack on it
       * or it does not.
       *
       * Only where ONE chip needs it. Two chips both reading "Others" would be
       * the duplicate-word defect all over again, so a genuine collision keeps
       * the product id — ugly, and the only stable handle left.
       */
      const unnamed = printings.items.filter(
        (q) => printingLabel(q) === base && !q.printingName && !q.rarity,
      ).length;
      if (unnamed === 1) return `${shown} — ${t("Others")}`;
      return `${shown} — #${p.productId}`;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printings.items, card.code, locale]);

  /*
   * THE ENTRY BEING LISTED: the set the seller chose, and everything inside it.
   *
   * The copies it holds, the drawing it settles on, and the drawings it proved —
   * all three arrive on the printing, because all three are facts about the SET
   * rather than about the card.
   */
  /*
   * A CHIP ANSWERS FOR EVERY COPY IN ITS SET, not only for the one it carries.
   *
   * REPORTED: "scanning a Basic Land 265 from M19 still fills the set as Core
   * 2019 and shows a big select for which printing is yours that also lists sets
   * but it's autofilled with 30th Anniversary Edition, which is incorrect."
   *
   * Both halves of that come from this one line. A chip stands for a SET and
   * carries its representative's id — the lowest in the set — while the scan
   * hands back the product it actually recognised, which is usually a different
   * member of the same set. So nothing matched, no chip was chosen, and the
   * form fell through to showing a set name from one place and a highlighted
   * chip from another.
   *
   * `versions` is the list of copies the chip stands for, which is exactly the
   * question being asked here.
   */
  const holds = (p, productId) => p.productId === productId
    || (p.versions || []).some((v) => v.productId === productId);
  const printingEntry = printings.items.find((p) => holds(p, picked.productId)) || null;
  /*
   * WHAT TO SUGGEST THE SELLER ASKS, from the printing they picked.
   *
   * THE 7-DAY AVERAGE, not the trend and not the lowest listing. A trend turns
   * on a single sale; "lowest" is whatever one person is dumping it for today
   * and is frequently ABOVE the trend, because sometimes nobody is selling
   * cheap. A week is recent enough to follow a market that moves and long
   * enough not to be one transaction.
   *
   * OUR OWN MARKET FIRST WHERE IT EXISTS, because what a card went for here is
   * a better answer than what it went for anywhere else. It is empty until
   * somebody completes a sale, so it leads when it is there and is simply
   * absent when it is not.
   *
   * THE LABEL SAYS THE SOURCE AND THE WINDOW AND NOTHING ELSE. Not a paragraph
   * about what Cardmarket is: a seller who trades cards knows, and a caveat
   * nobody asked for reads as an excuse for the number.
   *
   * FOIL FOLLOWS THE BOX, because a foil is a different card at a different
   * price. Worth knowing before this surprises somebody: Cardmarket's foil
   * figure is LOWER than its plain one for 73% of Yu-Gi-Oh printings, against
   * 10% of Magic's — their foil column does not mean the same thing in every
   * game. The number shown is theirs either way; this is a suggestion and the
   * seller knows their own game.
   */

  /*
   * AND THE BOX STAYS EMPTY, which is where this ended up after going the other
   * way for an afternoon.
   *
   * The owner asked for a seeded price and then corrected the scope: "the price
   * box auto-fill was meant for the card scan feature, not for the normal way of
   * creating a listing". That distinction is the right one. Somebody who has
   * chosen a printing from a picker has already thought about what they hold and
   * can price it; somebody who has just pointed a camera at a card has done
   * neither, and filling the last box is the whole point of that flow.
   *
   * So the argument the old test made still stands HERE: a price we typed is a
   * number nobody checked, and a marketplace whose boxes open at our figure is
   * priced by us rather than by the people in it. The suggestions below remain
   * one tap away.
   *
   * THE SCANNER IS GONE (2026-10-08), so the exception this paragraph carves out
   * no longer has anything on the other side of it: the box is empty for
   * everybody, which is where the reasoning pointed all along. Kept as written
   * because the argument is the reason, and the reason outlived the feature.
   */

  /*
   * AND THE SAME ENTRY, ONLY WHERE THERE IS A PICKER TO CHANGE IT WITH.
   *
   * This is what the narrowing is captioned by — "Only the ones printed in The
   * Best Vol.2." — and a caption naming a set nobody can move off is a dead end
   * rather than an explanation. A card with a single entry draws no chips, so
   * there is no way to widen the list again, so it is not said to be narrowed.
   *
   * Narrow when it can be proved, never hide when it cannot: the server matches
   * Cardmarket's set name against the publisher's series name and offers what it
   * proves, and where it proves nothing it offers everything. A filter that
   * guessed would hide the drawing actually in the seller's hand, which is a
   * dead end; an unfiltered list costs a longer read of tiles that each name the
   * series they came from.
   */
  const chosenPrinting = printings.items.length > 1 ? printingEntry : null;
  /*
   * THE COPIES OF THIS CARD INSIDE THE CHOSEN SET — the one merged control.
   *
   * A tile is a Cardmarket VERSION where the set holds more than one product,
   * and a DRAWING where it holds one and the publisher drew the card more than
   * once. Either way it is one question with one answer. Empty where the set
   * holds one product and one drawing, which is 7,064 of the 9,412 One Piece
   * (card, set) pairs a seller can be asked about: nothing is drawn and nothing
   * is asked.
   */
  const versions = printingEntry?.versions || [];
  // A tile is a product, and a product id is unique inside a set.
  const versionKeyOf = (v) => `${v.productId}`;
  /*
   * NOTHING UNTIL SOMEBODY CLICKS. A default would be a claim about an object
   * only the seller can see — the rule the artwork picker always had, now
   * applied to the merged control, because its tiles carry drawings too.
   */
  const chosenVersion = versions.find((v) => versionKeyOf(v) === versionKey) || null;
  /*
   * WHAT THE LISTING ACTUALLY STORES.
   *
   * The chosen version's product where one was chosen, because that is the whole
   * point of it: the chip above names a SET and its `productId` is only that
   * set's representative. Falls back to the chip's own id otherwise — a real
   * Cardmarket id, in the right set, of the right card, claiming nothing about
   * which copy inside it.
   */
  const sourceProductId = chosenVersion?.productId ?? picked.productId;

  /*
   * FOLLOWING CARDMARKET FROM THE MOMENT IT IS POSTED — migration 091.
   *
   * Offered here as well as on the edit dialog because THIS is where the seller
   * knows: they are holding the card, they have just chosen which printing it
   * is, and they are deciding what to charge. Making them post first and then
   * go and find the listing to edit is asking them to do the same thinking
   * twice.
   *
   * The fields are the same component the edit dialog uses, so the three
   * options, the bounds and the preview cannot come apart between the two
   * screens.
   */
  const [market, setMarket] = React.useState({ followsMarket: false, adjust: null, basis: 'low', priceCents: null, valid: true });

  /*
   * Cardmarket's low price for THIS printing, and for this copy of it.
   *
   * Re-asked when the foil box is ticked, which is the reason it is fetched
   * rather than carried on the printing: a foil is priced separately, so the
   * same product has two answers and the number on screen must follow the
   * checkbox. Without that, ticking "foil" would leave a stale price sitting
   * under the preview looking entirely plausible.
   */
  const [marketLow, setMarketLow] = React.useState(null);
  const [marketAvg, setMarketAvg] = React.useState(null);
  React.useEffect(() => {
    if (!sourceProductId || f.mode === 'trade') { setMarketLow(null); setMarketAvg(null); return; }
    const controller = new AbortController();
    api.catalogPrice({ productId: sourceProductId, foil: !!f.foil }, controller.signal)
      // A price we could not fetch is shown as no price, which is the same
      // sentence as a card the source does not price — and true either way
      // from the seller's point of view.
      .then((r) => { setMarketLow(r.lowCents ?? null); setMarketAvg(r.avgCents ?? null); })
      .catch(() => { setMarketLow(null); setMarketAvg(null); });
    return () => controller.abort();
  }, [sourceProductId, f.foil, f.mode]);

  /*
   * DECLARED HERE, AFTER THE FETCH IT READS, and the position is load-bearing.
   *
   * It sat two hundred lines higher, above `marketLow`, and moving its source
   * to that fetch put a `const` reference before its initialiser — a temporal
   * dead zone, which `useMemo` walks straight into because its callback runs
   * during the render that defines it. Every test of the posting screen went
   * red at once: "Cannot access 'marketLow' before initialization". The
   * typechecker and the unbound-identifier check both passed, because both
   * questions are about whether the name EXISTS.
   */
  const suggestions = React.useMemo(() => {
    /*
     * OUR OWN figure still comes from the printing — there is no per-product
     * endpoint for it and it is a 7-day average over sales here, which does not
     * differ between two copies of one set the way Cardmarket's price does.
     * Cardmarket's two come from the fetch below instead; see the note there.
     */
    const sug = printingEntry?.suggestedPrice;
    const out = [];
    if (!sug && !(marketLow > 0) && !(marketAvg > 0)) return out;
    // The foil's own number where the box is ticked and the source has one.
    // A foil with no foil price falls back to the plain one rather than to
    // nothing: an approximate suggestion beats an empty row.
    const pick = (o) => (o ? (f.foil ? o.foilCents ?? o.cents : o.cents) : null);
    const ours = pick(sug && sug.ours);
    if (ours > 0) out.push({ key: 'ours', cents: ours, source: t("Sold here · 7-day average") });
    /*
     * CARDMARKET'S LOW AND AVERAGE, in that order.
     *
     * Two numbers rather than one, on the owner's decision. A single
     * suggestion is a recommendation and has to be the defensible number; a
     * low beside an average is a RANGE, and the person holding the card knows
     * whether they want it gone this week better than we do.
     *
     * Low first because it is the smaller of the two and because it is the
     * number the market-following switch below uses — a seller should meet it
     * here, once, before deciding to hand their price over to it.
     */
    /*
     * FROM THE PRICE FETCHED FOR THE CHOSEN PRODUCT, not from the printing.
     *
     * REPORTED: "the price suggestions do not update when I select different
     * versions of the card. The price shown in the Cardmarket sync block does
     * update though." Exactly right, and the two halves had different sources:
     * `suggestedPrice` hangs off the SET-level entry, which does not change when
     * a seller picks a different copy INSIDE that set, while the sync block
     * fetches the price of `sourceProductId` — the version actually chosen.
     *
     * So both now read the same fetch. That is not only a fix but the removal
     * of the disagreement: two numbers on one screen claiming to be Cardmarket's
     * low, differing, with nothing to tell a seller which to believe.
     *
     * It follows the foil box for free, because that fetch already does.
     */
    if (marketLow > 0) out.push({ key: 'cm-low', cents: marketLow, source: t("Cardmarket · low") });
    if (marketAvg > 0) out.push({ key: 'cm-avg', cents: marketAvg, source: t("Cardmarket · average") });
    return out;
  }, [printingEntry, f.foil, marketLow, marketAvg, t]);

  /*
   * THE PRICE BOX FOLLOWS THE RULE, once there is a rule.
   *
   * Reported: "it's weird that I see 'Cardmarket: €0.02 You charge €0.04' but
   * then I don't see the price as 0.04." The listing really is born at that
   * number — the create route applies the rule in the same transaction — so a
   * box showing the old number was the screen disagreeing with itself, with
   * both halves looking equally authoritative.
   *
   * WRITTEN RATHER THAN MERELY DISPLAYED, and the box goes read-only below, so
   * there is one number on screen and it is the one that will be used. Typing
   * under a switch that overwrites what you type is the worse version of this.
   *
   * ONLY WHEN THERE IS A PRICE TO FOLLOW. A card the catalogue does not price
   * keeps what the seller typed — that is the documented behaviour, and the box
   * stays theirs.
   */
  const followedPrice = market.followsMarket ? market.priceCents : null;
  React.useEffect(() => {
    if (followedPrice == null) return;
    set('price', api.moneyText(String(followedPrice / 100).replace('.', ',')));
    // `set` is stable enough for this: it closes over the setter, not the value.
  }, [followedPrice]);
  /*
   * WHERE THE WORDS ON THE TILES CAME FROM: Cardmarket's own page, the
   * catalogue's name for the printing, or our count of the set. One source for
   * the whole control — the server never mixes them — so the first tile answers
   * for all of them.
   *
   * Read as a source rather than a boolean because the difference is a factual
   * claim on screen: only the first of the three lets us tell a seller to go and
   * check the word against Cardmarket's page.
   */
  const versionLabels = versions[0]?.labelSource ?? null;
  // And whether the catalogue holds a scan of each copy, which decides whether
  // this control is pictures or words.
  const versionsHaveImages = versions.some((v) => v.imageUrl);
  /*
   * THE WAITING STATE, and it takes the shape of what replaces it.
   *
   * Only where the suggestion already said the publisher draws this card more
   * than once, because that is the case whose tiles are pictures and therefore
   * the case where the grid has a size worth reserving. A set's products arrive
   * with the same response and cost nothing to lay out late.
   */
  const versionsWaiting = printings.loading && card.count > card.sets;
  /*
   * A CEILING ON THE VERSION GRID, and it is a reveal rather than a dropdown.
   *
   * The printings can become a `<select>` because that question is answered by a
   * NAME. This one is answered by a drawing wherever it can be, and a list of
   * "ST15-002_p2" in a dropdown answers nothing at all — so the picker stays
   * tiles whatever the count, and what changes above VERSION_TILE_LIMIT is how
   * many are laid out at once. The same short-list-then-reveal ChipPicker uses
   * for the languages.
   *
   * A CHOSEN TILE IS NEVER HIDDEN. If the answer somebody already gave sits past
   * the cut, the whole grid opens from the first render — a picker that has
   * quietly folded away the tile it is showing as chosen is the worst state this
   * control can be in.
   *
   * Sticky once opened, like ChipPicker: collapsing a grid back under somebody
   * mid-decision is a control that moves while it is being used.
   */
  const [allPrintingsShown, setAllPrintingsShown] = React.useState(false);
  const [allVersionsShown, setAllVersionsShown] = React.useState(false);
  const versionChosenBeyondCut = versions
    .slice(VERSION_TILE_LIMIT)
    .some((v) => versionKeyOf(v) === versionKey);
  const shownVersions = allVersionsShown || versionChosenBeyondCut
    ? versions
    : versions.slice(0, VERSION_TILE_LIMIT);

  /*
   * The same cut for the printing tiles, and the same escape from it.
   *
   * ONLY WHERE THERE ARE PICTURES. Chips are cheap to draw and cheap to fetch —
   * they fetch nothing — and above twelve of them this picker is already a
   * `Select`. It is the picture layout that puts sixty-eight files on a screen.
   *
   * A PRINTING ALREADY CHOSEN FROM BEYOND THE CUT KEEPS THE LIST OPEN, exactly as
   * `versionChosenBeyondCut` does above: collapsing the list under somebody's own
   * answer would hide the tile that is currently marked as chosen, and the screen
   * would then be showing a selection nobody can see.
   */
  const printingChosenBeyondCut = printings.items
    .slice(PRINTING_TILE_LIMIT)
    .some((p) => p.productId === picked.productId);
  const printingsCut = showPrintingImages && !allPrintingsShown && !printingChosenBeyondCut;
  const shownPrintings = printingsCut
    ? printings.items.slice(0, PRINTING_TILE_LIMIT)
    : printings.items;

  const [files, setFiles] = React.useState([]);
  const previews = React.useMemo(() => files.map((file) => ({ file, url: URL.createObjectURL(file) })), [files]);
  React.useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);
  const fileInput = React.useRef(null);
  /* The camera's own input — see the note beside it. */
  const cameraInput = React.useRef(null);

  // A photo is no longer required. It was, and the database enforced it, until an
  // imported inventory turned "photograph each of 400 cards first" into the reason
  // a trader's stock was not on the site at all — see migration 047. The form
  // still asks, because a photo of the actual card is what somebody is judging and
  // it is what the browse filter looks for; it just no longer refuses.
  /*
   * A game is required now that nothing is selected by default.
   *
   * It always was, on the server. It used to be satisfied by a default nobody
   * had chosen, which is how somebody could post a Magic card filed under One
   * Piece without ever touching the chips.
   *
   * A language is required for the same reason and in the same way. The server
   * refuses a post without one since migration 050, so leaving it out of this
   * gate would only move the refusal from a sentence on the screen to a toast
   * after the photographs had uploaded.
   */
  /*
   * AND A PRINTING, since 2026-10-12. The server refuses a listing that names
   * none (`listing_needs_a_catalog_product`, 422) and the rule is written out
   * beside the card field above; this is the half that says so before the
   * photographs have uploaded rather than in a toast after them. `sourceProductId`
   * is the chip's own product or the chosen copy's — either is a real printing.
   */
  /*
   * AND THE TITLE HAS TO STILL BE THE PICKED CARD'S NAME. A pick writes the
   * suggestion's name into the title; a seller who then types over it is
   * describing a different card, and the printing picked a moment ago is no
   * claim about that one. Without this, "Hajrudin" picked and "Hajrudin promo
   * 2027" typed would post under Hajrudin's printing — the exact listing the
   * rule exists to refuse. The suggestions reopen as they type, and picking
   * again is one tap.
   */
  const titleIsThePick = pickedTitle != null && f.title.trim() === pickedTitle.trim();
  const ready = f.game && f.language && f.title.trim() && sourceProductId && titleIsThePick && (f.mode === 'trade' || (f.price && !priceError)) && market.valid;
  /*
   * The card as it will look once posted, built from the form rather than from
   * the API — so it has to carry the SAME shape a listing view carries, and that
   * shape is `priceCents`. The field holds text a person typed, which is the one
   * place in the app where money is not already cents; `priceCents` above is
   * that text read once, and both this preview and the body sent to the server
   * take it from there rather than each reading the string again. A preview that
   * parsed for itself is a preview that can quote a price the listing will not.
   */
  /*
   * WHAT THE CARD WILL LOOK LIKE, IN THE SAME ORDER THE SERVER RESOLVES IT.
   *
   * The chosen drawing, then the drawing the SET settles on, and the catalogue's
   * own picture only where the card has one drawing to be. Two places deciding
   * this differently is how a preview comes to show something the listing will
   * not, so this is `imageFor` in `routes/listings.ts` written out in the same
   * order — and the create route stores the settled drawing, so the listing
   * shows the picture this promised.
   *
   * AND NOTHING AT ALL WHERE WE CANNOT SAY WHICH COPY IT IS. A set that holds
   * several copies and no scan to tell them apart has nothing worth previewing
   * until the seller clicks one, and the chip's own picture would be a guess
   * between them.
   */
  /*
   * The preview draws this at 298 CSS px, so it takes the 480-wide copy where
   * one exists — `ListingCard` has a `src` and no `srcset`, so the choice a
   * browser would make is made here. Falls through to the original in the same
   * order as before wherever there are no variants, which keeps the three-way
   * precedence below exactly as it was.
   */
  /*
   * WHETHER THE PICTURE THIS CHIP CARRIES IS A GUESS.
   *
   * The SERVER's answer, not a count done here. It is true only where the
   * catalogue holds one file for a card code the publisher drew several times
   * and no printing of ours settled which — and it is the same rule `pictureFor`
   * applies when the advert is rendered. A preview that promised a picture the
   * listing then withheld is the two screens disagreeing, which is the defect
   * this whole area keeps being corrected for.
   *
   * NOT "the copies look different". A set selling the card twice under two
   * scans is a real question, and the seller answers it by clicking a tile — but
   * until they do, the chip's own scan is a true picture of a real printing of
   * that set, and the advert will show it. Blanking there would hide something
   * we can stand behind.
   */
  const pictureIsAGuess = Boolean(printingEntry?.pictureIsAGuess);
  const previewImage = api.pickWidth(chosenVersion?.imageSrcset, 480) || chosenVersion?.imageUrl
    || (pictureIsAGuess ? null : (api.pickWidth(picked.imageSrcset, 480) || picked.imageUrl));
  const preview = { image: previewImage || undefined, title: f.title || 'Card name', set: f.set || f.game, priceCents: priceCents == null ? undefined : priceCents, condition: f.condition, km: 0, mode: f.mode === 'trade' ? 'trade' : 'sale' };

  /**
   * A card chosen from the catalogue, wherever the choosing happened.
   *
   * Hoisted out of the autocomplete because the SCANNER seeds the same form, and
   * two copies of "what picking a card means" would drift — the first thing to
   * go would be one of the four resets below, and the symptom is a Zoro advert
   * wearing a Luffy picture.
   */
  const pickCard = (item) => {
    setCard({
      productId: item.productId, code: item.cardCode,
      // Printings AND sets, because the picker draws one entry per
      // set wherever the pictures cannot separate two products of
      // one — and the waiting state has to be laid out at the size
      // of what replaces it before either list exists.
      count: item.printings || 1, sets: item.sets || 0,
      imagesDiffer: Boolean(item.printingImagesDiffer),
    });
    setPicked({ productId: item.productId, imageUrl: item.imageUrl, imageSrcset: item.imageSrcset || null });
    setPickedTitle(suggestionNameFor(item));
    setPickedName(item.name);
    // A different card, so whatever copy was claimed for the last one
    // is not a claim about this one. Carrying it over is how a Zoro
    // advert ends up wearing a Luffy picture.
    setVersionKey(null);
    setVersionDropped(false);
    setAllPrintingsShown(false);
    setAllVersionsShown(false);
    setF((prev) => ({
      ...prev,
      title: suggestionNameFor(item),
      game: item.game || prev.game,
      // Fill only an empty set line — someone who has described the
      // printing themselves meant it.
      //
      // `setName` is the set of the product this suggestion POINTS AT,
      // which is the correction. It used to be the most-printed
      // expansion of the whole group, chosen independently of the
      // product id beside it, so the two started out disagreeing before
      // anybody had touched anything.
      set: prev.set || lineFor(item.setName, item.cardCode) || prev.set,
    }));
  };


  return (
    <>
      <BackLink />
      {/* Not "within 15 km": the radius that decides who sees this is the READER's,
          set on their own account, not the poster's. Naming a number here promised
          something we do not control. */}
      <SectionHead title={t("Post a card")}
        sub={t("Anyone close enough to come and get it will see it. No listing fee, no shipping label.")} />
      <div className="cbm-detail cbm-detail--form">
        <div className="cbm-stack">
          <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span className="cbm-label">{t("Game")}</span>
              <div className="cbm-wrap">{GAMES.map((g) => <Tag key={g.id} selected={f.game === g.id} onClick={() => set('game', f.game === g.id ? '' : g.id)}>{g.label}</Tag>)}</div>
            </div>
            {/*
              THE CARD IS CHOSEN FROM THE LIST, AND A TITLE ON ITS OWN DOES NOT POST.

              This said the opposite for a long time — "what was typed always
              stands on its own: the catalogue does not know every promo or
              misprint, and a card should never be unpostable because a
              suggestion list has not heard of it" — and the owner overrode it
              on 2026-10-12: "todos los anuncios deben estar ligados a un
              blueprint o producto real de un tcg." The API refuses a listing
              with no printing since the same day (`listing_needs_a_catalog_product`),
              and `ready` below never offers one.

              The reason is what the free-text exit cost while it was open. A
              listing with no printing behind it cannot be priced from the
              catalogue, cannot show the right picture, cannot be matched to a
              want, and cannot be merged on a restock — posting the same card
              twice made two listings instead of adding copies. Production held
              61 listings and none used the exit; pre held one real use, a promo
              the catalogue already carried under seven product ids.

              The genuine gap — a set we have never ingested — is real, and its
              answer is the missing-set report that sits on this same form: under
              the printings for a card we know, and beside the post button for
              one we do not. Picking from the catalogue still fills the set line,
              which is the part people get wrong or leave blank.
            */}
            <CardAutocomplete
              label={t("Card")}
              placeholder="Charizard"
              value={f.title}
              // Whatever the chips say, and nothing when they say nothing. The
              // search and the highlight read the same value, so they cannot
              // disagree — which is the bug this replaces: One Piece rendered as
              // selected while the search deliberately ignored it.
              game={f.game || undefined}
              onChange={(v) => set('title', v)}
              onPick={pickCard}
            />
            {/*
              THE SET IS ASKED ONCE, BY ONE CONTROL, AND THAT CONTROL IS ALWAYS
              EDITABLE.

              TWO REPORTS GOT US HERE and the second corrects the first.

              "Either we show the 'Set and edition' input as an input or we show
              it as a select if there's too many options. But not both." — so the
              box and the picker stopped both being on screen.

              "I checked and the listing form still has the set as an
              unmodifiable label. That's wrong. We should preselect the one we
              think it is but not block it." — and that is the part I got wrong
              closing the first one. A read-only line is not a third kind of
              control, it is the ABSENCE of one: the seller reads a set they
              cannot correct, and the only way out is to change the card.

              It bit exactly where it was least visible. A card printed in ONE
              set draws no printing picker at all, so the line was the whole
              answer — Leblanc, Deceiver said "Unleashed" and there was nothing
              on the screen that could say otherwise.

              SO: a dropdown where there are sets to choose between, a text box
              where there are not, and never a label. Both are filled in with
              what we believe, which is the "preselect" half; both can be
              changed, which is the half that was missing.
            */}
            {printingsPicker ? null : (
              <Input
                label={t("Set and edition")}
                placeholder={t("Base Set · Holo · 1999")}
                value={f.set}
                onChange={(e) => set('set', e.target.value)}
                hint={picked.productId
                  /* Said out loud where we filled it in, because a box that
                     arrives with an answer already in it reads as locked unless
                     something tells you otherwise. */
                  ? t("We filled this in from the card you picked. Change it if it is wrong.")
                  : t("Whatever is printed on the card is enough.")} />
            )}
            {/*
              WHICH printing, and what a printing IS depends on the game.

              Where the publisher draws a card once per printing — most of Magic —
              two products can share a name and an expansion and differ only in
              the picture, so the picture has to be on offer and the entry is a
              product.

              Where it does not, there is nothing on a product a seller can
              recognise. One Piece maps every product sharing a card code onto
              that code's single artwork, so "The Best Vol.2" arrived twice with
              nothing between the two but 0,57 EUR against 22,93 EUR — a price
              doing an identity's job. The server now sends one entry per SET for
              those, and the seller answers the two questions that CAN be
              answered: which set, and then which artwork.

              Both product ids are still real and one of them is still stored.
              What is refused is the guess about which of them the seller holds.
            */}
            {/* `items.length > 1`, not `>= 1`: the suggestion's count groups by NAME
                and this endpoint groups by name AND game, so the 136 names the
                catalogue shares across two games can promise two printings and
                deliver one. A picker offering a single choice is not a choice. */}
            {/* `card.count > 1` guards the WAITING state, not the list. The
                request is now made when either question exists, so a card with
                three artworks and one printing sets `printings.loading` too — and
                without this it would lay out a single waiting tile and then take
                it away, moving everything under it. */}
            {/*
              SHOWN FOR ONE SET TOO, which it was not, and that is what put a raw
              text box on the screen for Leblanc: "We said long ago that the set
              should be a list of chips or a select when there's more than n
              options to choose from. Here we just show an input."

              The old guard was `> 1`, under a comment reading "a picker offering
              a single choice is not a choice". True while this was only asking
              WHICH PRINTING — but it is the set control now, and a control that
              disappears when there is one answer leaves nothing to show the
              answer in. The seller then gets a text box, which is the shape we
              use for "we have never heard of this card" and reads as one.

              One chip is not a choice. It is a statement of what the catalogue
              holds, in the same shape as the other five games' answers, with the
              way out underneath rather than instead.
            */}
            {(printings.loading && card.count > 1) || printings.items.length >= 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/*
                  A QUESTION ONLY WHERE THERE IS SOMETHING TO ASK. One chip is
                  the catalogue stating which set it holds, and "Which printing
                  is yours?" over a single answer reads as a demand to choose
                  between one thing.
                */}
                <span className="cbm-label">
                  {usePrintingSelect || printings.items.length < 2
                    ? t("Set and edition")
                    : t("Which printing is yours?")}
                </span>
                <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {printings.items.length < 2 && !printings.loading
                    ? t("The only set our catalogue holds this card in.")
                    : usePrintingSelect
                      ? t("Which set is it printed in? It sets the picture and the printing a buyer's search matches.")
                      : t("Pick the one that looks like the card in your hand. It sets the picture, the set line and the printing a buyer's search matches.")}
                </p>
                {/*
                  The waiting state is the same shape as what replaces it — same
                  control, same count, same flow — so nothing under it moves when
                  the printings land. `card.count` comes back with the suggestion
                  for exactly this reason, and `printings.total` carries it here.
                */}
                {/*
                  THREE LAYOUTS, one picker, and the data picks between them.

                  Where the printings differ by PICTURE, a tile is a picture with
                  a caption under it, because the picture is the answer and no
                  sentence stands in for it. That is Magic — 19,880 of its 22,805
                  multi-product name groups — and it is untouched.

                  Where they do not, the question is only ever "which set is it",
                  and a set name is a WORD. So it is a row of chips, the same
                  control the games and the languages already use. What stood here
                  was a full-width strip per printing carrying the rarity and the
                  price: the shape of a picture picker with the picture removed,
                  which read as a table of numbers rather than a list of names —
                  and it put the price where the eye looks for the difference,
                  which is the thing this picker must not do.

                  Above PRINTING_CHIP_LIMIT the chips become a dropdown, because
                  thirteen set names is a wall and 178 of them is a screenful.
                */}
                {usePrintingSelect ? (
                  <div className="cbm-printings cbm-set-select" aria-busy={printings.loading || undefined}>
                    {/*
                      Disabled and empty while it waits rather than absent: a
                      control that appears at full height a moment later moves
                      every field under it, and this one is the same height at 178
                      printings as it is at 13.

                      The question is already written above it, so it is carried
                      as the accessible name rather than printed a second time.
                    */}
                    <Select
                      aria-label={t("Which printing is yours?")}
                      disabled={printings.loading}
                      value={printings.loading ? '' : selectedPrintingValue}
                      options={printings.loading
                        ? [{ value: '', label: '\u00a0' }]
                        : printings.items.map((p) => ({ value: String(p.productId), label: printingOptionLabel(p) }))}
                      onChange={(value) => {
                        const p = printings.items.find((x) => String(x.productId) === value);
                        if (p) choosePrinting(p);
                      }}
                    />
                  </div>
                ) : (
                <div className="cbm-printings"
                  style={showPrintingImages
                    ? { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }
                    // Chips wrap; they do not stack. A column of full-width rows
                    // is a table, and there is nothing here to line up in columns.
                    : { display: 'flex', flexWrap: 'wrap', gap: 6 }}
                  aria-busy={printings.loading || undefined}>
                  {(printings.loading
                    // Capped where the layout caps itself, so the number of
                    // waiting shapes is the number of real ones that replace them.
                    // That is the whole reason this number is not simply "lots":
                    // it used to be 200, which drew sixty-eight shapes and then
                    // sixty-eight tiles, and it now draws twelve of each.
                    ? Array.from(
                      { length: Math.min(printingCount, showPrintingImages ? PRINTING_TILE_LIMIT : PRINTING_CHIP_LIMIT) },
                      (_, i) => ({ productId: `wait-${i}`, waiting: true }),
                    )
                    : shownPrintings
                  ).map((p) => {
                    // Chosen when this chip holds the picked product, whether as
                    // its representative or as one of the copies it stands for.
                    // See `holds`.
                    const chosen = !p.waiting && holds(p, picked.productId);
                    return (
                      <button
                        key={p.productId}
                        type="button"
                        disabled={Boolean(p.waiting)}
                        aria-pressed={p.waiting ? undefined : chosen}
                        onClick={() => choosePrinting(p)}
                        style={{
                          textAlign: 'left', font: 'inherit', cursor: p.waiting ? 'default' : 'pointer',
                          ...(showPrintingImages
                            ? {
                              width: 116, display: 'flex', flexDirection: 'column', gap: 6, padding: 6,
                              background: chosen ? 'var(--surface-sunken)' : 'var(--white)',
                              border: `1px solid ${chosen ? 'var(--action-secondary)' : 'var(--border-hairline)'}`,
                              borderRadius: 'var(--radius-md)',
                            }
                            // A chip, in the design system's own shape: pill, ink
                            // when it is the chosen one, hairline when it is not.
                            // It is a real <button> rather than the DS `Tag`,
                            // which renders a <span> — this question is answered
                            // with `aria-pressed` and from a keyboard, and a chip
                            // that a keyboard cannot reach is not a chip.
                            //
                            // Wrapping, not `nowrap`: the median set name on this
                            // branch is 21 characters and the long tail runs to
                            // "Starter Deck: Edward.Newgate (Non-English)", which
                            // nowrap would push off the side of a phone.
                            : {
                              // `center`, not `baseline`. REPORTED: "It seems to
                              // have a greater spacing under the text than over
                              // it." Exactly what baseline alignment does inside
                              // a box with a `minHeight` bigger than the line:
                              // the text sits on its baseline near the top and
                              // every spare pixel collects underneath.
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              maxWidth: '100%', padding: '7px 12px', minHeight: 34,
                              font: 'var(--type-ui)', fontSize: 'var(--text-xs)',
                              color: chosen ? 'var(--white)' : 'var(--ink-700)',
                              background: chosen ? 'var(--ink-900)' : 'var(--white)',
                              border: `1px solid ${chosen ? 'var(--ink-900)' : 'var(--border-default)'}`,
                              borderRadius: 'var(--radius-pill)',
                              transition: 'var(--transition-control)',
                            }),
                        }}
                      >
                        {showPrintingImages ? (
                          <span style={{ display: 'block', width: '100%', aspectRatio: '5 / 7', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                            {p.imageUrl ? (
                              /* 102 CSS px, fixed at every viewport: this row
                                 reflows rather than resizing. A picture with no
                                 variants sends a null srcset and renders `src`,
                                 which is the full-size file it always was. */
                              <img src={p.imageUrl} srcSet={p.imageSrcset || undefined} sizes="102px"
                                alt="" loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            ) : null}
                          </span>
                        ) : null}
                        {/* Rendered while waiting too, blank and at the width of
                            a median set name, so a chip does not grow its text
                            the moment the list lands and reflow the row. */}
                        <span style={{
                          font: 'var(--type-ui)', fontSize: 'var(--text-xs)',
                          color: showPrintingImages ? 'var(--text-strong)' : 'inherit',
                          ...(p.waiting && !showPrintingImages ? { display: 'inline-block', width: 132 } : null),
                        }}>
                          {p.waiting ? '\u00a0' : printingShown(p)}
                        </span>
                        {showPrintingImages ? (
                          /*
                           * The rarity, and NOT the price.
                           *
                           * The owner: "We should not use suggestion prices in
                           * the app until we are sure each price matches each
                           * product." They are right, and this screen is where
                           * it mattered most: two rows of the same set, told
                           * apart by nothing but a number we cannot yet vouch
                           * for, invites somebody to read the expensive one as
                           * the card they own. The reference price returns when
                           * we can say which product it belongs to.
                           */
                          <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {p.waiting ? '\u00a0' : (p.rarity || '\u00a0')}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                )}
                {/* The reveal, modelled on the versions picker's below rather
                    than invented a second time — a reader who meets both on one
                    screen should meet one affordance.

                    Asked of the LIST, so it never appears promising more and
                    doing nothing when tapped, and it says the NUMBER: "show
                    more" beside twelve tiles does not tell anybody whether there
                    are two behind it or fifty-six. Lightning Bolt has 68. */}
                {!printings.loading && shownPrintings.length < printings.items.length ? (
                  <div>
                    <Button variant="outline" size="sm" onClick={() => setAllPrintingsShown(true)}>
                      {t("Show all {count} printings", { count: printings.items.length })}
                    </Button>
                  </div>
                ) : null}
                {!printings.loading && ambiguousPrintings ? (
                  /* THE SAME TILE TWICE, admitted rather than papered over.

                     Only under pictures, and only because that layout still has
                     the problem: two tiles carrying the same set, the same rarity
                     and the same drawing, with the price as the one visible
                     difference — which is a number doing an identity's job.

                     The chips no longer reach here. A set that Cardmarket sells
                     twice arrives as ONE entry, because a seller picks a set and
                     then picks an artwork, and neither of those questions is
                     answered by a product number. */
                  <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {t("Some of these are the same set twice. Cardmarket sells them as separate cards but does not say what differs, so the price beside each is only a reference.")}
                  </span>
                ) : null}
                {!printings.loading && printings.total > printings.shown ? (
                  /* Said out loud rather than serving a short list as the whole
                     one. Only the basic lands reach this — nine name groups in the
                     catalogue hold more than two hundred printings, and all nine
                     are Forest, Island and their four siblings.

                     Counted in PRINTINGS on both sides. A chip can stand for
                     several of them, so comparing the total against the number of
                     chips would announce a truncation on every One Piece card
                     that has none: eight chips, ten printings, nothing cut. */
                  <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {t("Showing {shown} of {total} printings.", { shown: printings.shown, total: printings.total })}
                  </span>
                ) : null}
                {/*
                  AND THE WAY OUT, under the sets rather than instead of them.

                  "We should preselect the one we think it is but not block it."
                  Chips preselect; this is the not-blocking. Somebody holding a
                  set we have never ingested — a new release, a regional print,
                  anything the catalogue is behind on — writes it down here, and
                  the picker steps aside for the text box.

                  A link rather than a button, because it is a way out of the
                  normal path and not a thing to do on the way through it.
                */}
                {/*
                  A DIALOG, the same one the picture report uses — asked for, and
                  right: the two are the same act from the seller's side. The
                  catalogue is wrong about the card in their hand and they are the
                  only person who can say so. Made to look alike so the second one
                  needs no learning once you have met the first.

                  See `set-report.jsx` for why this is a report at all rather than
                  the text box it replaced.
                */}
                <SetReport productId={card.productId} game={f.game} />
              </div>
            ) : null}
            {/*
              WHICH COPY OF THIS CARD — ONE CONTROL, BECAUSE IT IS ONE QUESTION.

              This screen used to carry two, side by side: "Which version is
              yours?" over the products of a set, and "Which artwork is yours?"
              over the drawings the publisher makes. The owner read them the way
              anybody would:

                "I am shown two kinds of new selection ... I believe those are
                 the same thing. When Cardmarket talks about 'versions', they
                 mean alternate art cards/reprints. Let's call them versions like
                 Cardmarket does and that's it."

              They are the same thing, and asking twice let the two answers
              disagree: nothing stopped a seller choosing V.1 above and the
              alternate art's picture below, and the listing stored both. One
              tile now carries the product AND the drawing, and one click stores
              them together.

              CARDMARKET'S WORD FOR IT IS A VERSION, and a version is a PRODUCT —
              so where a set holds more than one, a tile is one of those, named
              V.1 and V.2 from their own marker where a trader's export has
              taught us one and from a count of the set where it has not. Where a
              set holds one product, Cardmarket sells one thing and has no number
              for it; the publisher can still have drawn the card more than once,
              and then the tiles are those drawings and the caption is the series
              each was printed in.

              THREE QUARTERS OF SETS DRAW NOTHING. 7,064 of the 9,412 One Piece
              (card, set) pairs hold one product and settle on one drawing; the
              seller is asked nothing and this renders nothing. The worst in the
              whole catalogue is ST03-008's eight products, which pair to no
              drawing at all and are offered as eight numbered tiles — a picture
              nothing proves is exactly what this control must not invent.

              NOTHING IS CHOSEN ON ARRIVAL. A default would be us claiming
              something about a card in somebody else's hand. Until a tile is
              pressed the preview shows a frame saying the picture is missing,
              which is both the honest state and the thing that asks for the
              answer.
            */}
            {/*
              AN ANSWER THAT IS NO LONGER ON OFFER IS GONE, AND GONE LOUDLY.

              OUTSIDE the control, deliberately, because the commonest way to
              lose an answer is to move to a set that asks nothing at all — three
              quarters of them — and a notice living inside the picker would
              vanish in exactly the case where the picker does. A hidden field
              disagreeing with a visible control is the class of defect 153e758
              and 4bb8afe both close; a dropped answer nobody is told about is
              the same defect with the field empty.
            */}
            {versionDropped ? (
              <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-strong)' }}>
                {t("The version you picked is not in this set, so it is no longer chosen.")}
              </p>
            ) : null}
            {versionsWaiting || versions.length > 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="cbm-label">{t("Which version is yours?")}</span>
                <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {/* Three sentences, and which one shows is not decoration.
                      Cardmarket's own number is one the seller can go and read on
                      their page; a counted one is ours, inferred from the order
                      the catalogue lists the products in; and where the tiles are
                      drawings there is no number at all, because Cardmarket sells
                      one product there and never named a second. Presenting any
                      of the three as another would be the screen claiming a fact
                      it does not have. */}
                  {versionLabels === 'reported'
                    ? t("This set holds more than one version of this card. These are Cardmarket's own version numbers, so you can check them against their page.")
                    : versionLabels === 'catalogue'
                      ? t("This set holds more than one version of this card. These are the names our catalogue gives them; Cardmarket may word them differently on their page.")
                      : t("This set holds more than one version of this card. Nobody has told us which is which, so they are numbered in the order the catalogue lists them.")}
                </p>
                {/* Same shape as what replaces it, so nothing under it moves when
                    the versions land. The count came back with the suggestion. */}
                <div className="cbm-versions"
                  style={versionsWaiting || versionsHaveImages
                    ? { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }
                    : { display: 'flex', flexWrap: 'wrap', gap: 6 }}
                  aria-busy={versionsWaiting || undefined}>
                  {(versionsWaiting
                    // Capped where the grid caps itself, so the waiting tiles are
                    // the count of the real ones that replace them.
                    ? Array.from({ length: Math.min(Math.max(card.count || 2, 2), VERSION_TILE_LIMIT) },
                      (_, i) => ({ productId: -i - 1, waiting: true }))
                    : shownVersions
                  ).map((v) => {
                    const chosen = !v.waiting && versionKeyOf(v) === versionKey;
                    // Pictures where the control has any, words where it has
                    // none — one layout for the whole grid, so eight numbered
                    // tiles do not sit in eight empty picture frames.
                    const pictured = Boolean(v.waiting || versionsHaveImages);
                    return (
                      <button
                        key={v.waiting ? `wait-${v.productId}` : versionKeyOf(v)}
                        type="button"
                        disabled={Boolean(v.waiting)}
                        aria-pressed={v.waiting ? undefined : chosen}
                        // Clicking the chosen one again unchooses it. The question
                        // is skippable, so there has to be a way back to unanswered
                        // once it has been answered.
                        onClick={() => { setVersionKey(chosen ? null : versionKeyOf(v)); setVersionDropped(false); }}
                        style={pictured
                          ? {
                            width: 116, textAlign: 'left', font: 'inherit',
                            cursor: v.waiting ? 'default' : 'pointer',
                            display: 'flex', flexDirection: 'column', gap: 6, padding: 6,
                            background: chosen ? 'var(--surface-sunken)' : 'var(--white)',
                            border: `1px solid ${chosen ? 'var(--action-secondary)' : 'var(--border-hairline)'}`,
                            borderRadius: 'var(--radius-md)',
                          }
                          : {
                            display: 'inline-flex', alignItems: 'center', padding: '7px 12px',
                            minHeight: 34, cursor: 'pointer',
                            font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)',
                            color: chosen ? 'var(--white)' : 'var(--ink-700)',
                            background: chosen ? 'var(--ink-900)' : 'var(--white)',
                            border: `1px solid ${chosen ? 'var(--ink-900)' : 'var(--border-hairline)'}`,
                            borderRadius: 'var(--radius-pill)',
                          }}
                      >
                        {pictured ? (
                          <span style={{ display: 'block', width: '100%', aspectRatio: '5 / 7', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                            {v.imageUrl ? (
                              /* Same 102 px tile as the printing picker above. */
                              <img src={v.imageUrl} srcSet={v.imageSrcset || undefined} sizes="102px"
                                alt="" loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            ) : null}
                          </span>
                        ) : null}
                        {/* Both captions are rendered while waiting too, empty.
                            A tile that grows two lines the moment the versions
                            land moves everything below it, and the waiting state
                            is supposed to be the shape of what replaces it.

                            Cardmarket's own number where a tile is a product, and
                            the publisher's words for where the drawing was
                            printed where it is a drawing. NOT the set line: that
                            belongs to the printing chosen above and the two are
                            different vocabularies for overlapping things.

                            Clamped to two lines at a fixed height, because the
                            series labels are the publisher's and they are not
                            short — "PREMIUM BOOSTER -ONE PIECE CARD THE BEST
                            vol.2- [PRB-02]" beside "[ST-15]" would otherwise make
                            two tiles in the same row different heights. */}
                        <span style={pictured ? {
                          font: 'var(--type-ui)', fontSize: 'var(--text-xs)', color: 'var(--text-strong)',
                          lineHeight: 1.3, height: '2.6em', overflow: 'hidden',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        } : { font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)' }}>
                          {v.waiting ? ' ' : (v.label || v.series || card.code || t("Version"))}
                        </span>
                        {pictured ? (
                          <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                            {v.waiting ? ' ' : (v.rarity || '—')}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                {/* The reveal, drawn only when it has something to reveal — asked
                    of the LIST rather than of the flag, so it never appears
                    promising more and doing nothing when tapped. It says the
                    NUMBER, because "show more" beside twelve tiles does not tell
                    anybody whether there are two behind it or forty. */}
                {!versionsWaiting && shownVersions.length < versions.length ? (
                  <div>
                    <Button variant="outline" size="sm" onClick={() => setAllVersionsShown(true)}>
                      {t("Show all {count} versions", { count: versions.length })}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <Select label={t("Condition")} wrapperStyle={{ flex: 1, minWidth: 180 }} value={f.condition} onChange={(v) => set('condition', v)}
                // t(), like its twins on the listing page and the profile. This
                // one was built by concatenation, which is how it slipped past
                // both the scanner and every reviewer: the grade names beside it
                // were already translated and this select showed them in English.
                options={GRADES.map((g) => ({ value: g, label: g + ' · ' + t(GRADE_NAMES[g]) }))} hint={t("Grade it as you would want it graded for you.")} />
              {/*
                Beside the condition, because they are the same kind of fact: both
                describe the object rather than the offer, and a Japanese print and
                an English one are different cards at different prices.

                Sorted by the name the READER sees, not by the stored tag — the
                list is seventeen long since migration 053, and alphabetical in
                your own language is the only order you can scan.

                There is no "Not sure" entry any more. It was the honest opt-out
                while the column was nullable; now that every card has a language
                (migration 050) it would be an answer that cannot be posted.

                What replaces it is a PROMPT, not an answer: the first entry
                carries no language, and picking it leaves the post button
                disabled. It has to exist. A <select> whose value matches none of
                its options does not render blank — the browser falls back to
                the first option, so the control would read "German" while the
                form held nothing, and clicking "German" to agree with it would
                fire no change event at all. A control that shows a language
                nobody chose is the exact bug this whole field is about.
              */}
              <Select label={t("Language")} wrapperStyle={{ flex: 1, minWidth: 180 }} value={f.language} onChange={(v) => set('language', v)}
                /*
                 * ONLY WHAT THIS GAME IS PRINTED IN. One Piece offers five
                 * languages rather than seventeen — Bandai has never printed a
                 * Hungarian card, so offering it can only produce a listing that
                 * is wrong.
                 *
                 * "Ever printed" rather than "printed today", because this is a
                 * secondhand market: a Russian Magic card from 2019 is a real
                 * object somebody owns, and Wizards stopping in 2022 does not
                 * make it unlistable.
                 *
                 * A language ALREADY CHOSEN stays in the list even if the game
                 * changes underneath it. Otherwise switching game silently
                 * empties a field that still holds a value, and the form would
                 * submit a language the control no longer shows.
                 */
                options={[{ value: '', label: t("Choose a language") }].concat(
                  CARD_LANGUAGES
                    .filter((code) => languagesForGames(f.game ? [f.game] : []).includes(code) || code === f.language)
                    .map((code) => ({ value: code, label: t(CARD_LANGUAGE_NAMES[code]) }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                )}
                /*
                 * THE HINT SAYS SO WHERE THE SET ANSWERED THIS.
                 *
                 * On the control that changed rather than in a banner above the
                 * form: the seller is looking at the language box precisely when
                 * they wonder why it says Japanese, and a notice three fields
                 * away is a notice they scroll past. It replaces the hint rather
                 * than joining it because the ordinary sentence — "the language
                 * printed on the card" — is advice for somebody choosing, and
                 * nobody is choosing here.
                 */
                hint={languageFromSet
                  ? t("Filled in from the set you picked, which is the {language} printing. Change it if your copy is not.",
                    { language: t(CARD_LANGUAGE_NAMES[languageFromSet]) })
                  : t("The language printed on the card, not the one you are reading.")} />
              {/*
                `inputMode="decimal"` and not `numeric`, which is what this was.
                A phone keyboard set to `numeric` offers DIGITS ONLY — no comma,
                no full stop — so on the device most of this audience posts from,
                a price under a euro would still have been untypeable with the
                parsing fixed. `decimal` is the one that puts a separator key on
                the pad. The quantity fields below and in the stock dialog stay
                `numeric` deliberately: half a card is not a thing.
              */}
              <Input label={t("Price")} wrapperStyle={{ flex: 1, minWidth: 180 }} suffix="€" inputMode="decimal" placeholder="2,40"
                disabled={f.mode === 'trade' || followedPrice != null}
                value={f.price}
                onChange={(e) => set('price', api.moneyText(e.target.value))}
                error={priceError}
                /* Said only while something else is filling it in, so the
                   ordinary case keeps its uncluttered field. */
                hint={followedPrice != null ? t("Cardmarket sets this price.") : undefined} />
              {/*
                HOW MANY, ON THE SAME LINE AND NARROW.
                
                It had a row to itself at 180px wide, which is a great deal of
                box for a "1" — and the row it sat in was mostly empty. A
                quantity is one or two digits; `flex: 0 0 auto` keeps it from
                being stretched to match its neighbours, and the width is set by
                the label above it rather than by the number inside.
                
                Digits only, and an empty box means one — which is what somebody
                who never touched this field meant.
              */}
              <Input label={t("Copies")} wrapperStyle={{ flex: '0 0 auto', width: 104 }} inputMode="numeric" placeholder="1"
                value={f.copies} onChange={(e) => set('copies', e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            {/*
              WHAT OTHERS ASK FOR THIS PRINTING — a suggestion, never a default.
              
              NOT PRE-FILLED, and that is the whole design. A price somebody
              typed is a decision; a price we typed for them is a number they did
              not check, and the first thing a seller would learn is that our box
              is usually about right — which is how a marketplace ends up priced
              by us instead of by the people in it.
              
              SO IT IS ONE TAP AND SAYS WHERE IT CAME FROM. Two markets, because
              they are genuinely different: Cardmarket is an online market with
              postage and buyer protection and every seller in Europe; ours is
              two people meeting in a cafe. Our own figure is the better one and
              is empty until somebody completes a sale — shown anyway, because a
              suggestion that appears one day without warning is a surprise, and
              because its absence is itself honest.
              
              THE LABEL IS THE SOURCE AND THE WINDOW, FULL STOP. No sentence
              explaining what Cardmarket is or why a local sale might differ: a
              person selling cards already knows, and a caveat nobody asked for
              reads as an apology for the number.
              
              ONLY FOR A SALE. A trade has no price to suggest.
            */}
            {f.mode !== 'trade' && suggestions.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <span style={{ font: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-label)', color: 'var(--text-muted)' }}>
                  {t("What this printing usually goes for")}
                </span>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  {suggestions.map((sug) => (
                    <button
                      key={sug.key}
                      type="button"
                      onClick={() => set('price', moneyBox(sug.cents))}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                        padding: 'var(--space-3) var(--space-4)', cursor: 'pointer',
                        background: 'var(--white)', border: '1px solid var(--border-default)',
                        borderRadius: 'var(--radius-sm)', textAlign: 'left',
                        font: 'var(--type-body)', color: 'var(--text-strong)',
                      }}
                    >
                      <span style={{ font: 'var(--type-body-strong)' }}>{api.formatCents(sug.cents)}</span>
                      <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{sug.source}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {/*
              FOLLOW CARDMARKET, decided while the card is still in your hand.

              Only for a sale, and only once a printing is chosen: a trade has
              no price to move, and a card the catalogue does not know has
              nothing to follow. The API refuses both, so this condition and
              that refusal say the same thing from two ends.

              BELOW the suggestions rather than above them on purpose. The
              suggestions are what to type; this is the decision to stop typing
              a price at all, which only makes sense once you have seen what the
              number would be.
            */}
            {f.mode !== 'trade' && sourceProductId ? (
              <div style={{ marginTop: 'var(--space-4)' }}>
                <MarketFollowFields lowCents={marketLow} avgCents={marketAvg} initial={null} onChange={setMarket} />
              </div>
            ) : null}
            {/*
              Foil, and only foil.

              Beside the condition and the language above because it is the same
              kind of fact — what the object IS, rather than what is being asked
              for it — and because a foil and a non-foil of the same printing are
              two different cards at two different prices. A buyer who finds out
              at the meeting was misled by an advert that had room to say so.

              NOT in the `ready` gate below, deliberately. Unticked is an answer
              and the commonest one; a checkbox that blocked the button until it
              was touched would be asking every seller to confirm a negative.

              The four other flags a Cardmarket export carries — signed, altered,
              first edition, reverse holo — have no control here on purpose. The
              owner's decision was that they sit in the notes for now, and the
              import writes them there.
            */}
            {/*
              SPACE ABOVE IT. The price suggestions sit directly over this, and a
              checkbox tight under a row of prices reads as part of them — as
              though ticking it did something to the number rather than
              describing the card.
              
              AND THE THREE WORDS PEOPLE ACTUALLY USE. It said "Foil" with the
              other two hidden in the small print underneath, which is the wrong
              way round: somebody scanning for the word THEY use should find it
              in the label.
            */}
            <Checkbox
              style={{ marginTop: 'var(--space-4)' }}
              label={t("Foil / Holo / Shiny")}
              description={t("A foil, holo or shiny printing of this card.")}
              checked={f.foil} onChange={(on) => set('foil', on)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span className="cbm-label">{t("How do you want to move it?")}</span>
              <Radio value={f.mode} onChange={(v) => set('mode', v)} options={[
                { value: 'sale', label: t("For sale"), description: 'A price, paid in person' },
                { value: 'trade', label: t("For trade"), description: 'Cards only — no price shown' },
              ]} />
            </div>
            <Input label={t("Notes for traders")} placeholder={t("Sleeved since 2011. Light edge wear on the back.")} value={f.note} onChange={(e) => set('note', e.target.value)} />
          </Card>

          {/*
            Photographs of the actual card.
            The catalogue picture shows what the card looks like in print, which
            is the same for every copy ever made. These show THIS one — and a
            condition claim with no picture behind it is just a word somebody
            typed. Required for that reason, not for decoration.
          */}
          <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div>
              <span className="cbm-label">{t("Photos of your card")}</span>
              <p style={{ margin: '6px 0 0', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("At least one. Front, back, and a close-up of any wear — this is what somebody is judging.")}
              </p>
            </div>
            {previews.length ? (
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                {previews.map((p, i) => (
                  <div key={p.url} style={{ position: 'relative', lineHeight: 0 }}>
                    <img src={p.url} alt="" style={{ width: 84, height: 108, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-hairline)' }} />
                    <div style={{ position: 'absolute', top: 2, right: 2 }}>
                      <IconButton icon="x" label={t("Remove this photo")}
                        onClick={() => setFiles(files.filter((_, n) => n !== i))} />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {/*
              `image/*` RATHER THAN A LIST OF FIVE TYPES, and that is the bug
              somebody hit on a phone: "it only lets me select an image from my
              device, but doesnt give me the option to take a photo now" — and
              then, on another handset, the button did nothing at all.

              Android offers the chooser whatever apps MATCH the accept types. A
              camera does not advertise `image/heif`, so it was never among them;
              and where no installed app matched the list, the chooser had
              nothing to show and appeared dead.

              Nothing is lost by loosening it: the browser's declared type was
              never the check. `lib/photos.ts` reads the real format out of the
              bytes and refuses anything that is not one of these five, which is
              the only check that was ever load-bearing.
            */}
            <input ref={fileInput} type="file" multiple style={{ display: 'none' }}
              accept="image/*"
              onChange={(e) => {
                setFiles(files.concat(Array.from(e.target.files).slice(0, 6 - files.length)));
                e.target.value = '';
              }} />
            {/*
              AND THE CAMERA, asked for by name. `capture` is the attribute that
              opens a phone's camera instead of its files; a laptop ignores it
              and shows the picker, which is the right answer on a laptop. The
              scanner has used exactly this as its fallback since it was written
              — this form simply never asked.

              One at a time, because a camera returns one photograph.
            */}
            <input ref={cameraInput} type="file" style={{ display: 'none' }}
              accept="image/*" capture="environment"
              onChange={(e) => {
                setFiles(files.concat(Array.from(e.target.files).slice(0, 6 - files.length)));
                e.target.value = '';
              }} />
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="outline" iconLeft="camera" disabled={files.length >= 6}
                onClick={() => cameraInput.current && cameraInput.current.click()}>
                {t("Take a photo")}
              </Button>
              <Button variant="outline" iconLeft="upload" disabled={files.length >= 6}
                onClick={() => fileInput.current && fileInput.current.click()}>
                {files.length ? t("Add another") : t("Add a photo")}
              </Button>
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Photos are stripped of their hidden data — including the location your phone records — before anyone else can see them.")}
              </span>
            </div>
          </Card>

          {/*
            The Picture and Meeting cards were both here and both went.

            Picture repeated the preview standing beside it — the same image, plus
            a paragraph explaining an image the reader can already see. Meeting was
            a checkbox that was checked, disabled, and could never be anything else:
            a control for a decision nobody gets to make, which reads as a choice
            and is not one. Where a listing has no stock picture that is now said
            once, in the preview, rather than in a section of its own.
          */}
          <div className="cbm-actionbar">
            {/* `priceCents`, not `price`: the euro float stops existing here
                too, for the reason written out beside `formatCents` — the unit
                belongs in the name at every call site, and this one used to hand
                the store `+f.price` for it to multiply by 100 at the other end. */}
            <Button size="lg" disabled={!ready} onClick={() => publishListing({ ...f, priceCents: f.mode === 'trade' ? undefined : priceCents, title: (titleIsThePick && pickedName) || f.title.trim(), set: f.set.trim() || f.game, quantity: Math.max(1, parseInt(f.copies, 10) || 1), sourceProductId: sourceProductId || undefined, followsMarket: market.followsMarket, marketAdjust: market.adjust, marketBasis: market.basis }, files)
              // The store rethrows so a caller CAN react; this one has nothing to
              // add, because it already told the person via a toast. Without this
              // every refused post becomes an unhandled rejection — invisible to
              // the person, noise in the console, and a failing "no page errors"
              // check for anybody who looks.
              .catch(() => undefined)}>{t("Post a card")}</Button>
            {/* "Save as draft" was here with no handler, and there is still
                nowhere to save one into. Drafts were built and then removed: the
                photographs are uploaded before the listing is created, so a
                listing without a picture is not something the API can be asked
                for, and the in-between state stopped being necessary. */}
            {!files.length && f.title.trim() ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("No photo? It still posts. Cards with one are the ones people filter for.")}
              </span>
            ) : null}
            {/* Said rather than left to a disabled button. Nothing is selected
                until somebody selects it, so "why can I not post this" has an
                answer on screen instead of in the chips they have scrolled past. */}
            {!f.game && f.title.trim() ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Choose the game to post it.")}
              </span>
            ) : null}
            {/* And the same for the language, which is required since migration
                050. Without this the only thing telling somebody why they cannot
                post is a button that does nothing. */}
            {!f.language && f.title.trim() ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Choose the language printed on the card to post it.")}
              </span>
            ) : null}
            {/*
              AND THE PRINTING, which is the rule of 2026-10-12: a title on its
              own no longer posts. One sentence with its reason, and the way out
              offered right here — the missing-set report with no product to
              hang it on, because the card itself is what we lack. Keyed on the
              title so a "thank you" about one card is not still standing when
              the next one is typed.
            */}
            {(!sourceProductId || !titleIsThePick) && f.title.trim() ? (
              <>
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("Pick the card from the list to post it: a card we do not have cannot be priced, pictured or matched, so tell us which set is missing instead.")}
                </span>
                <SetReport key={f.title.trim()} productId={null} game={f.game} />
              </>
            ) : null}
          </div>
        </div>

        {/*
          Shown on every width, not just desktop.
          It was `cbm-desk`, which was survivable while a separate Picture section
          repeated the image inside the form — remove that and somebody posting on
          a phone had no picture at all, and no way to tell whether the card they
          picked was the right one. The preview IS the confirmation, so it has to be
          where the posting actually happens, and most of it happens on a phone.
          Sticky only where there is room to be sticky.
        */}
        <div className="cbm-stack cbm-post-preview">
          <span className="cbm-label">{t("How it will look")}</span>
          <ListingCard {...api.listingCardProps(preview, t)} />
          {/*
            THE MOMENT SOMEBODY ACTUALLY KNOWS THE PICTURE IS WRONG.
            
            They are holding the card, the catalogue has just shown them what it
            believes the card looks like, and the two do not match. Asking them
            here costs one tap; asking on the listing page asks a BUYER, who has
            never seen the object.

            Only where the picture is OURS: `previewImage` is null while a card
            with several drawings has none chosen, and a photograph they upload
            is theirs to be wrong about, not ours.

            AND FOR EVERY GAME NOW, WHICH IT WAS NOT. The second condition was
            `card.code`, and that quietly excluded four games: 11,850 of One
            Piece's 12,421 listable products carry a code and Magic, Yu-Gi-Oh!,
            Pokemon and Lorcana carry ZERO between them — 285,000 products where
            this control never appeared, and nobody had decided that.

            Invisible rather than harmful while those games had no reliable
            picture to be wrong about. Migration 072 gives the report and the
            override a second subject — the PRODUCT, which names a printing, and
            a printing is what a picture belongs to.
          */}
          {previewImage ? (
            <PictureReport
              game={f.game}
              cardCode={card.code}
              productId={sourceProductId}
              kind="wrong"
            />
          ) : null}
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{t("Traders see the distance from their own radius, not your address.")}</span>
        </div>
      </div>
    </>
  );
}

